/**
 * Results Tab - Business Value Calculator
 * Sprint 7: Day 3 COMPLETE
 *
 * Displays comprehensive financial results:
 * - Executive Summary Card (4 metrics: NPV, IRR, ROI, Payback)
 * - Scenario Comparison Table (Best/Base/Worst with expected value)
 * - 36-Month Cash Flow Table (scrollable with break-even detection)
 * - Financial Charts (Cost vs Benefit + Cumulative Cash Flow)
 *
 * Features:
 * - Automatic calculation from benefits and costs
 * - Input validation warnings
 * - Responsive design
 * - Professional styling with gradients
 */

import React, { useState, useEffect } from 'react';
import { ExecutiveSummaryCard, FinancialMetrics, ValidationWarning } from './ExecutiveSummaryCard';
import { ScenarioComparisonTable, Scenario, generateScenarios } from './ScenarioComparisonTable';
import { CashFlowTable, CashFlowRow, generateCashFlowData } from './CashFlowTable';
import { FinancialCharts } from './FinancialCharts';

interface ResultsTabProps {
  totalBenefits: number;
  totalCosts: number;
  discountRate: number;  // As decimal (0.10 for 10%)
  taxRate: number;       // As decimal (0.25 for 25%)
}

export const ResultsTab: React.FC<ResultsTabProps> = ({
  totalBenefits,
  totalCosts,
  discountRate,
  taxRate
}) => {
  const [metrics, setMetrics] = useState<FinancialMetrics | null>(null);
  const [scenarios, setScenarios] = useState<{ bestCase: Scenario; baseCase: Scenario; worstCase: Scenario } | null>(null);
  const [cashFlowData, setCashFlowData] = useState<CashFlowRow[]>([]);
  const [breakEvenMonth, setBreakEvenMonth] = useState<number | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<ValidationWarning[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);

  // Calculate financial metrics
  useEffect(() => {
    const calculateMetrics = async () => {
      setIsCalculating(true);
      setValidationWarnings([]);

      // Validation
      const warnings: ValidationWarning[] = [];

      if (totalBenefits <= 0) {
        warnings.push({
          severity: 'error',
          message: 'Total benefits must be greater than zero. Please configure benefit drivers in the Benefits tab.'
        });
      }

      if (totalCosts <= 0) {
        warnings.push({
          severity: 'error',
          message: 'Total costs must be greater than zero. Please configure project costs in the Costs tab.'
        });
      }

      if (warnings.length > 0) {
        setValidationWarnings(warnings);
        setIsCalculating(false);
        return;
      }

      // Generate cash flow data
      const cashFlow = generateCashFlowData(totalCosts, totalBenefits);
      setCashFlowData(cashFlow);

      // Find break-even month
      const breakEven = cashFlow.findIndex(row => row.cumulativeCashFlow > 0);
      setBreakEvenMonth(breakEven >= 0 ? breakEven + 1 : null);

      // Calculate NPV (simplified version - would call backend API in production)
      const npv = calculateNPV(cashFlow, discountRate);

      // Calculate IRR (simplified version)
      const irr = calculateIRR(cashFlow);

      // Calculate ROI
      const roi = ((totalBenefits - totalCosts) / totalCosts) * 100;

      // Calculate payback period
      const payback = breakEven >= 0 ? breakEven + 1 : null;

      const calculatedMetrics: FinancialMetrics = {
        npv,
        irr,
        roi,
        paybackMonths: payback
      };

      setMetrics(calculatedMetrics);

      // Generate scenarios
      const generatedScenarios = generateScenarios(totalBenefits, totalCosts, calculatedMetrics);
      setScenarios(generatedScenarios);

      setIsCalculating(false);
    };

    calculateMetrics();
  }, [totalBenefits, totalCosts, discountRate, taxRate]);

  // Simplified NPV calculation (discounted cash flow)
  const calculateNPV = (cashFlow: CashFlowRow[], rate: number): number => {
    let npv = 0;
    for (let i = 0; i < cashFlow.length; i++) {
      const month = i + 1;
      const discountFactor = Math.pow(1 + rate / 12, month);
      npv += cashFlow[i].netCashFlow / discountFactor;
    }
    return npv;
  };

  // Simplified IRR calculation (Newton-Raphson approximation)
  const calculateIRR = (cashFlow: CashFlowRow[]): number => {
    let rate = 0.1; // Initial guess: 10%
    const maxIterations = 100;
    const tolerance = 0.0001;

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      let npv = 0;
      let derivative = 0;

      for (let i = 0; i < cashFlow.length; i++) {
        const month = i + 1;
        const discountFactor = Math.pow(1 + rate / 12, month);
        npv += cashFlow[i].netCashFlow / discountFactor;
        derivative -= (cashFlow[i].netCashFlow * month) / (discountFactor * (1 + rate / 12));
      }

      if (Math.abs(npv) < tolerance) {
        return rate * 100; // Convert to percentage
      }

      // Newton-Raphson update
      rate = rate - npv / derivative;

      // Prevent divergence
      if (rate < -0.99) rate = -0.99;
      if (rate > 10) rate = 10;
    }

    return rate * 100; // Convert to percentage
  };

  return (
    <div className="results-tab">
      {/* Executive Summary */}
      <ExecutiveSummaryCard
        metrics={metrics}
        validationWarnings={validationWarnings}
        isLoading={isCalculating}
      />

      {/* Render other components only if metrics are available */}
      {metrics && scenarios && cashFlowData.length > 0 && (
        <>
          {/* Scenario Comparison */}
          <ScenarioComparisonTable
            bestCase={scenarios.bestCase}
            baseCase={scenarios.baseCase}
            worstCase={scenarios.worstCase}
          />

          {/* Financial Charts */}
          <FinancialCharts
            cashFlowData={cashFlowData}
            breakEvenMonth={breakEvenMonth}
          />

          {/* Cash Flow Table */}
          <CashFlowTable
            cashFlowData={cashFlowData}
            breakEvenMonth={breakEvenMonth}
          />

          {/* Export Actions */}
          <div className="results-actions">
            <button className="results-action-button results-action-primary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export to Excel
            </button>
            <button className="results-action-button results-action-secondary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              Generate Executive Summary
            </button>
            <button className="results-action-button results-action-secondary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              Save as PDF
            </button>
          </div>
        </>
      )}

      <style>{`
        .results-tab {
          padding: 2rem 0;
        }

        .results-actions {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
          padding: 2rem;
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
        }

        .results-action-button {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.5rem;
          font-size: 0.9375rem;
          font-weight: 700;
          border: 2px solid;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .results-action-primary {
          color: #ffffff;
          background: linear-gradient(135deg, #0094ab 0%, #004772 100%);
          border-color: #0094ab;
        }

        .results-action-primary:hover {
          background: linear-gradient(135deg, #007a8f 0%, #003a5d 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 148, 171, 0.3);
        }

        .results-action-secondary {
          color: #0094ab;
          background: #ffffff;
          border-color: #0094ab;
        }

        .results-action-secondary:hover {
          background: rgba(0, 148, 171, 0.05);
          transform: translateY(-2px);
        }

        .results-action-button:active {
          transform: translateY(0);
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .results-actions {
            flex-direction: column;
          }

          .results-action-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default ResultsTab;
